import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton, Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useDeleteApplicationMutation,
  useExportReferralApplicationsMutation,
  useReferralApplicationsListQuery,
} from "api/applications/applications.mutation";
import applicationCountIcon from "assets/admin/applications_count.svg";
import excelIcon from "assets/admin/excel.svg";
import filterIcon from "assets/admin/filter.svg";
import { ConfirmDeleteModal } from "components/ConfirmDeleteModal";
import SwitchField from "components/SwitchField";
import { Table } from "components/Table";
import { handleError } from "functions/apiHandleError";
import { downloadBlob } from "functions/downloadBlob";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Link, useSearchParams } from "react-router-dom";
import useStore from "store";
import { ApplicationFilterModal } from "./components/applicationFilterModal";
import classes from "./style.module.scss";

const defaultFormFields = {
  age_min: 0,
  age_max: 80,
  university_status: "",
  faculty_status: "",
  country: "",
  degree: "",
  faculty: "",
  field_of_study: "",
  group: "",
  agent: "",
  email: "",
  gender: "",
  create_at_after: "",
  create_at_before: "",
  search: "",
  more_than_one_request: false,
  archived: false,
};

const MyApplication = () => {
  const { userInfoStore } = useStore();
  const { control, getValues, reset, watch, resetField, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      age_min: 0,
      age_max: 80,
      university_status: "",
      faculty_status: "",
      country: "",
      degree: "",
      faculty: "",
      field_of_study: "",
      group: "",
      agent: "",
      email: "",
      gender: "",
      create_at_after: "",
      create_at_before: "",
      search: "",
      more_than_one_request: false,
      archived: false,
    },
  });
  const [searchParams] = useSearchParams();
  const archived = watch("archived");

  const [reload, setReload] = useState(false);
  const [pageFilter, setPageFilter] = useState({
    page: 1,
    page_size: 15,
    total: 1,
    sort: "-create_at",
    count: 0,
  });
  const [filterModal, setFilterModal] = useState({
    open: false,
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, selectedApplication: null });
  const filters = { ...getValues(), archived };
  const referralApplicationsQuery = useReferralApplicationsListQuery({
    page: pageFilter.page,
    page_size: pageFilter.page_size,
    ordering: pageFilter.sort,
    filters,
    reload,
  });
  const deleteApplicationMutation = useDeleteApplicationMutation();
  const exportReferralApplicationsMutation = useExportReferralApplicationsMutation();
  const tableData = useMemo(
    () =>
      (referralApplicationsQuery.data?.results ?? []).map((row, index) => ({
        ...row,
        index: (pageFilter.page - 1) * pageFilter.page_size + index + 1,
      })),
    [referralApplicationsQuery.data, pageFilter.page, pageFilter.page_size]
  );
  const totalPages = referralApplicationsQuery.data?.total ?? pageFilter.total;
  const applicationsCount = referralApplicationsQuery.data?.count_all ?? pageFilter.count;

  const handleOpenFilterModal = () => {
    setFilterModal({ open: true });
  };

  const handleCloseFilterModal = () => {
    setFilterModal({ open: false });
  };

  const handleDeleteApplication = async () => {
    const applicationID = deleteModal.selectedApplication?.id;
    if (!applicationID) return;

    try {
      await deleteApplicationMutation.mutateAsync(applicationID);
      setDeleteModal({ open: false, selectedApplication: null });
    } catch (err) {
      handleError({ err });
    }
  };

  const getExportedData = async () => {
    try {
      const filters = { ...getValues(), archived };
      const response = await exportReferralApplicationsMutation.mutateAsync({
        ordering: pageFilter.sort,
        filters,
      });
      downloadBlob(response, "export_orders.xlsx");
    } catch (error) {}
  };

  useEffect(() => {
    let valueChanged = false;
    for (const [key, value] of searchParams.entries()) {
      if (key in defaultFormFields) {
        let typedValue = value;
        if (typeof defaultFormFields[key] === "number") {
          typedValue = Number(value);
        } else if (typeof defaultFormFields[key] === "boolean") {
          typedValue = value === "true";
        }
        valueChanged = true;
        setValue(key, typedValue);
      }
    }
    if (valueChanged) {
      setReload((prev) => !prev);
    }
  }, [searchParams]);

  const columns = [
    {
      field: "index",
      headerName: "ردیف",
      minWidth: 60,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "tracking_id",
      headerName: "شماره پرونده",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "full_name",
      headerName: "نام دانشجو",
      renderCell: ({ row }) => row?.user?.full_name,
      minWidth: 120,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "passport_number",
      headerName: "شماره پاسپورت",
      renderCell: ({ row }) => row?.user?.passport_number,
      minWidth: 120,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "gender",
      headerName: "جنسیت",
      minWidth: 60,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.user?.gender,
    },
    {
      field: "age",
      headerName: "سن",
      minWidth: 50,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.user?.age.toLocaleString("fa-ir"),
    },
    {
      field: "country",
      headerName: "کشور",
      minWidth: 110,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.user?.country,
    },
    {
      field: "gpa",
      headerName: "نمره",
      minWidth: 110,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => (
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column" }}>
          <Typography variant="caption">{`کارشناسی: ${row?.bachelor_gpa}`}</Typography>
          <Typography variant="caption">{`ارشد: ${row?.master_gpa}`}</Typography>
        </div>
      ),
    },
    {
      field: "degree",
      headerName: "مقطع",
      minWidth: 60,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "faculty",
      headerName: "رشته",
      minWidth: 80,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "field_of_study",
      headerName: "گرایش",
      minWidth: 130,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "group",
      headerName: "گروه",
      minWidth: 110,
      flex: 1,
      sortable: false,
      resizable: false,
    },
    {
      field: "jalali_created_at",
      headerName: "تاریخ درخواست",
      minWidth: 100,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => (
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column" }}>
          <Typography variant="caption">{row?.jalali_created_at?.split(" - ")[0]}</Typography>
          <Typography variant="caption">{row?.jalali_created_at?.split(" - ")[1]}</Typography>
        </div>
      ),
    },
    {
      field: "status",
      headerName: "وضعیت",
      minWidth: 80,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => row?.status,
    },
    {
      field: "action",
      headerName: "",
      minWidth: 80,
      flex: 1,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => (
        <>
          {userInfoStore.is_superuser && (
            <Tooltip title="حذف پرونده">
              <IconButton
                color="error"
                onClick={() => setDeleteModal({ open: true, selectedApplication: row })}
              >
                <DeleteIcon className={classes.nextIcon} fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Link
            className={classes.nextBox}
            to={`/dashboard/all-files/detail/${row?.id}`}
            target="_blank"
          >
            <ArrowBackIosNewIcon className={classes.nextIcon} fontSize="small" />
          </Link>
        </>
      ),
    },
  ];

  return (
    <>
      <div className={classes.mainDiv}>
        <div className={classes.headerTable}>
          <Stack
            divider={<Divider orientation="vertical" flexItem />}
            spacing={2}
            direction="row-reverse"
          >
            <div className={classes.exportBox}>
              <div className={classes.exportMethods}>
                <LazyLoadImage className={classes.Icon} src={excelIcon} onClick={getExportedData} />
              </div>
              <Typography className={classes.settingTableText}>دریافت فایل گزارش:</Typography>
            </div>
            <div className={classes.sortBox}>
              <div className={classes.sortMethods}>
                <Typography
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "-bachelor_gpa" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "-bachelor_gpa" })}
                >
                  بیشترین نمره کارشناسی
                </Typography>
                <Typography
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "-master_gpa" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "-master_gpa" })}
                >
                  بیشترین نمره ارشد
                </Typography>
                <Typography
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "-create_at" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "-create_at" })}
                >
                  جدیدترین
                </Typography>

                <Typography
                  className={`${classes.sortItemText} ${
                    pageFilter.sort === "create_at" && classes.sortItemTextActive
                  }`}
                  onClick={() => setPageFilter({ ...pageFilter, sort: "create_at" })}
                >
                  قدیمی ترین
                </Typography>
              </div>
              <Typography className={classes.settingTableText}>نمایش بر اساس:</Typography>
            </div>
            <div className={classes.filterBox}>
              <div className={classes.filterIconBox}>
                <LazyLoadImage
                  onClick={handleOpenFilterModal}
                  className={classes.filterIcon}
                  src={filterIcon}
                />
              </div>
              <Typography className={classes.settingTableText}>فیتلر</Typography>
            </div>
            <div className={classes.itemBox}>
              <SwitchField
                name="archived"
                control={control}
                label="پرونده های بایگانی"
                size="small"
              />
            </div>
          </Stack>
          <div className={classes.countApplicaton}>
            <Typography className={classes.countApplicationText}>
              <span>{applicationsCount.toLocaleString("fa-ir")}</span> پرونده
            </Typography>
            <LazyLoadImage className={classes.countApplicationImg} src={applicationCountIcon} />
          </div>
        </div>
        <Table
          rows={tableData}
          columns={columns}
          totalPages={totalPages}
          loading={referralApplicationsQuery.isFetching || deleteApplicationMutation.isPending}
        />
      </div>
      <ApplicationFilterModal
        open={filterModal.open}
        onClose={handleCloseFilterModal}
        reload={reload}
        setReload={setReload}
        reset={reset}
        control={control}
        watch={watch}
        resetField={resetField}
      />
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, selectedApplication: null })}
        onConfirm={handleDeleteApplication}
        loading={deleteApplicationMutation.isPending}
        title="حذف پرونده"
        message="آیا از حذف پرونده انتخاب‌شده مطمئن هستید؟ این عملیات قابل بازگشت نیست."
      />
    </>
  );
};

export default MyApplication;
